import * as React from 'react';

import {useDragItemContext} from '../DragItem/DragItemWrapper';

import {RowItemContainer} from './RowItem.styles';
import {RowItemContext} from './RowItemContext';
import {RowItemView, ROW_ITEM_MARGIN_VAR} from './RowItemBody';
import {RowItemActionsPortal} from './RowItemActions';
import {RowItemChevron} from './RowItemChevron';

import type {RowItemProps} from './_types';

export const RowItem = React.forwardRef<HTMLDivElement, RowItemProps>(
  (
    {
      children,
      checkmarkStyle = 'default',
      density = 'comfortable',
      hasChevron = false,
      _isActive: isActive,
      isDraggedItem,
      isRounded = false,
      isSelected,
      _listIndex,
      prefix,
      onBlur,
      onFocus,
      onMouseEnter,
      onMouseLeave,
      /**
       * onPointerLeave is a handler provided by FloatingUI that sets the active index to null.
       *
       * We're destructuring onPointerLeave to prevent it from being passed to RowItemBody.
       * If we don't destructure it here, it will cause the active index to be set to null each
       * time the pointer leaves RowItemBody and enters RowItemActions.
       *
       * Instead, we want to pass it to RowItemContainer so that the RowItem is considered active
       * until the pointer leaves the entire RowItemContainer.
       */
      onPointerLeave,
      onRenameOpenChange: onRenameOpenChangeProp,
      isRenaming: isRenamingProp,
      shouldAllowRenaming,
      ...itemPropsWithoutRef
    }: RowItemProps,
    ref
  ) => {
    const {isDragging} = useDragItemContext();
    const {isDisabled, shouldAlwaysShowActions} = itemPropsWithoutRef;

    const rowItemLabelRef = React.useRef<HTMLDivElement>(null);
    const rowItemInputPortalRef = React.useRef<HTMLDivElement>(null);

    const [isRenamingState, setIsRenamingState] = React.useState(false);

    const isRenaming = isRenamingProp ?? isRenamingState;

    const onRenameOpenChange = React.useCallback<
      NonNullable<RowItemProps['onRenameOpenChange']>
    >(
      (nextValue, event, reason) => {
        if (nextValue && !shouldAllowRenaming) {
          return;
        }

        if (isRenamingProp !== undefined && onRenameOpenChangeProp) {
          onRenameOpenChangeProp(nextValue, event, reason);
        } else {
          setIsRenamingState(nextValue);
        }
      },
      [
        isRenamingProp,
        onRenameOpenChangeProp,
        setIsRenamingState,
        shouldAllowRenaming,
      ]
    );

    /**
     * This ref is used to measure the Adornments, Actions, and Chevron.
     */
    const rowAdornmentsRef = React.useRef<HTMLDivElement>(null);
    /**
     * This ref is used to create the Portal for the Actions.
     */
    const rowActionsPortalRef = React.useRef<HTMLDivElement>(null);

    const [rowItemMargin, setRowItemMargin] = React.useState<
      string | undefined
    >(undefined);

    const toggleWidth = React.useCallback((entries: ResizeObserverEntry[]) => {
      const row = entries[0];
      if (row) {
        if (row.contentRect.width === 0) {
          setRowItemMargin('0px');
        } else {
          /**
           * If any Adornments, Actions, or Chevron are present, we need to adjust our RowItemLabel margin.
           * This will cause the label to move over as if these items were inline; truncating the label if necessary.
           *
           * We are adding 8px to give space between the RowItemLabel and any Adornments or Actions.
           */
          setRowItemMargin(`${row.contentRect.width + 8}px`);
        }
      }
    }, []);

    React.useEffect(() => {
      /**
       * We can't use the `useResizeObserver` hook here, since it doesn't seem to play well with `react-live` on the Doc Site.
       * We need to use the `ResizeObserver` directly.
       */
      if (rowAdornmentsRef.current) {
        const observer = new ResizeObserver(toggleWidth);
        observer.observe(rowAdornmentsRef.current);

        return () => {
          observer.disconnect();
        };
      }
    }, [toggleWidth]);

    const style = React.useMemo(() => {
      return {
        [ROW_ITEM_MARGIN_VAR]: rowItemMargin,
      } as React.CSSProperties;
    }, [rowItemMargin]);

    const contextValue = React.useMemo(
      () => ({
        checkmarkStyle,
        density,
        isActive,
        isDragging,
        isDraggedItem,
        isRenaming,
        isRounded,
        isSelected,
        onRenameOpenChange,
        shouldAllowRenaming,
        prefix,
        rowActionsPortalRef,
        rowItemLabelRef,
        rowItemInputPortalRef,
        itemProps: {
          ...itemPropsWithoutRef,
          ref,
        },
      }),
      [
        checkmarkStyle,
        density,
        isActive,
        isDragging,
        isDraggedItem,
        isRenaming,
        isRounded,
        isSelected,
        onRenameOpenChange,
        shouldAllowRenaming,
        prefix,
        rowActionsPortalRef,
        itemPropsWithoutRef,
        ref,
      ]
    );

    return (
      <RowItemContext.Provider value={contextValue}>
        <RowItemContainer
          data-active={isActive}
          data-disabled={isDisabled}
          data-list-index={_listIndex}
          isCheckbox={itemPropsWithoutRef._isCheckbox}
          isDragging={isDragging}
          onBlur={onBlur}
          onFocus={onFocus}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onPointerLeave={onPointerLeave}
          role="presentation"
          style={style}
        >
          {children}
          <div ref={rowItemInputPortalRef} />
          <RowItemActionsPortal
            density={density}
            ref={rowAdornmentsRef}
            role="presentation"
          >
            {/** We are using a span here instead of a View for performance reasons. */}
            <span ref={rowActionsPortalRef} style={{display: 'flex'}} />
            {hasChevron && !isRenaming ? (
              <RowItemChevron
                dragState={isDragging || isDraggedItem}
                isDisabled={isDisabled}
                shouldHideOnActive={!shouldAlwaysShowActions}
              />
            ) : null}
          </RowItemActionsPortal>
        </RowItemContainer>
      </RowItemContext.Provider>
    );
  }
);

RowItem.displayName = 'RowItem';
RowItem.toString = RowItemView.toString;

export default RowItem;
